/**
 * Esri's own "Reshape Feature" tool, reimplemented in plain GeoJSON
 * coordinates: sketch a brand new path across the part of a line or
 * polygon you want to change, and the part of the original boundary
 * between wherever your sketch starts and ends is replaced by it —
 * everything outside that span is untouched. This is deliberately NOT
 * vertex-dragging (`ol/interaction/Modify`, still used elsewhere in this
 * app) — the whole point of the Esri tool is redrawing a whole stretch of
 * boundary in one sketch, not relocating one point at a time.
 */

type Coord = [number, number];

interface ProjectedPoint {
  /** Index of the segment this point falls on: segment i connects path[i] to path[i + 1]. */
  segmentIndex: number;
  /** 0..1 position along that segment. */
  t: number;
  point: Coord;
}

function distance(a: Coord, b: Coord): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

/** The closest point on an open path (no wraparound) to `target`. */
function nearestPointOnPath(path: Coord[], target: Coord): ProjectedPoint {
  let best: ProjectedPoint = { segmentIndex: 0, t: 0, point: path[0] };
  let bestDistance = Infinity;
  for (let i = 0; i < path.length - 1; i += 1) {
    const a = path[i];
    const b = path[i + 1];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const lengthSq = dx * dx + dy * dy;
    const t = lengthSq === 0 ? 0 : Math.max(0, Math.min(1, ((target[0] - a[0]) * dx + (target[1] - a[1]) * dy) / lengthSq));
    const point: Coord = [a[0] + t * dx, a[1] + t * dy];
    const d = distance(target, point);
    if (d < bestDistance) {
      bestDistance = d;
      best = { segmentIndex: i, t, point };
    }
  }
  return best;
}

function pathLength(path: Coord[]): number {
  let total = 0;
  for (let i = 0; i < path.length - 1; i += 1) total += distance(path[i], path[i + 1]);
  return total;
}

/** Arc length from the start of `path` up to a projected point on it. */
function cumulativeLength(path: Coord[], upTo: ProjectedPoint): number {
  let total = 0;
  for (let i = 0; i < upTo.segmentIndex; i += 1) total += distance(path[i], path[i + 1]);
  total += upTo.t * distance(path[upTo.segmentIndex], path[upTo.segmentIndex + 1]);
  return total;
}

/** Drops consecutive duplicate points — the seam where a cut point exactly
 *  coincides with an existing vertex, or a sketch end lands exactly on one. */
function dedupe(points: Coord[], eps = 1e-9): Coord[] {
  const result: Coord[] = [];
  for (const point of points) {
    const last = result[result.length - 1];
    if (!last || Math.abs(last[0] - point[0]) > eps || Math.abs(last[1] - point[1]) > eps) {
      result.push(point);
    }
  }
  return result;
}

/** Replaces the span of `path` between `start` and `end` (start assumed to
 *  come first) with `middle`, keeping everything outside that span as-is. */
function spliceOpenPath(path: Coord[], start: ProjectedPoint, end: ProjectedPoint, middle: Coord[]): Coord[] {
  const before = path.slice(0, start.segmentIndex + 1);
  const after = path.slice(end.segmentIndex + 1);
  return dedupe([...before, start.point, ...middle, end.point, ...after]);
}

/**
 * Reshapes an open line: the span between whichever two points on `original`
 * sit nearest the sketch's own two ends is replaced by the sketch — both
 * ends of the original line are left exactly as they were.
 */
export function reshapeLine(original: Coord[], sketch: Coord[]): Coord[] {
  if (original.length < 2 || sketch.length < 2) return original;

  const endA = nearestPointOnPath(original, sketch[0]);
  const endB = nearestPointOnPath(original, sketch[sketch.length - 1]);
  const posA = endA.segmentIndex + endA.t;
  const posB = endB.segmentIndex + endB.t;

  return posA <= posB
    ? spliceOpenPath(original, endA, endB, sketch)
    : spliceOpenPath(original, endB, endA, sketch.slice().reverse());
}

/**
 * Reshapes a polygon ring. A ring has two possible arcs between the
 * sketch's two ends rather than a line's one — the shorter arc (by real
 * length, not vertex count) is always the one replaced, which is what
 * makes sketching a small notch or bulge near one edge do what it looks
 * like it should, instead of requiring the sketch to trace most of the
 * polygon's boundary.
 */
export function reshapeRing(ring: Coord[], sketch: Coord[]): Coord[] {
  if (ring.length < 4 || sketch.length < 2) return ring;

  const closed = distance(ring[0], ring[ring.length - 1]) < 1e-9;
  const unique = closed ? ring.slice(0, -1) : ring.slice();
  const m = unique.length;
  if (m < 3) return ring;

  const endA = nearestPointOnPath([...unique, unique[0]], sketch[0]);
  const segA = endA.segmentIndex;

  // Re-expressed as an open path that starts and ends at A, walking every
  // original vertex exactly once — turns the ring's "two arcs" ambiguity
  // into a single ordinary open-path splice, the same one `reshapeLine` does.
  const loop: Coord[] = [endA.point, ...unique.slice(segA + 1), ...unique.slice(0, segA + 1), endA.point];

  const endB = nearestPointOnPath(loop, sketch[sketch.length - 1]);
  const total = pathLength(loop);
  const forwardLength = cumulativeLength(loop, endB);
  const backwardLength = total - forwardLength;
  const start: ProjectedPoint = { segmentIndex: 0, t: 0, point: endA.point };

  if (forwardLength <= backwardLength) {
    // The short arc runs forward from A to B — replace it with the sketch,
    // keeping the rest of the loop (B back around to A) as it was.
    return spliceOpenPath(loop, start, endB, sketch);
  }

  // The short arc is the OTHER one (B back around to A) — keep A..B as
  // original, and swap in the (reversed, so it runs B -> A) sketch for the
  // rest of the loop instead of splicing a middle span.
  const head = loop.slice(0, endB.segmentIndex + 1);
  return dedupe([...head, endB.point, ...sketch.slice().reverse(), endA.point]);
}

/** Dispatches to `reshapeLine`/`reshapeRing` by the original geometry's own
 *  type — the one entry point `EditPanels.tsx` needs. Only LineString and
 *  Polygon make sense to reshape this way (a Point has no boundary to
 *  redraw a span of); anything else is left untouched. */
export function reshapeGeometryWithSketch(
  original: GeoJSON.Geometry,
  sketch: Coord[],
): GeoJSON.Geometry | null {
  if (sketch.length < 2) return null;

  if (original.type === 'LineString') {
    return { type: 'LineString', coordinates: reshapeLine(original.coordinates as Coord[], sketch) };
  }

  if (original.type === 'Polygon') {
    const rings = original.coordinates as Coord[][];
    const [outer, ...holes] = rings;
    const reshapedOuter = reshapeRing(outer, sketch);
    if (reshapedOuter.length < 4) return null;
    return { type: 'Polygon', coordinates: [reshapedOuter, ...holes] };
  }

  return null;
}
